import React, { useEffect, useMemo, useState } from "react";
import { useMediaQuery, useTheme } from "@mui/material";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { useFilterState, useSortingState } from "../../hooks/useScrollPosition";
import { getEcdEzeScrubColumns } from "./columnsConfig/ecdEzeScrubColumns";
import { getEcdGenevaScrubColumns } from "./columnsConfig/ecdGenevaScrubColumns";
import { fetchEzeEcdScrubData, fetchGenevaEcdScrubData } from "../../services/Axios";

const ECDScrubEze = ({
  data,
  title = "ECD Scrub Eze",
  globalFilter: initialGlobalFilter = "",
  setGlobalFilter: setParentGlobalFilter,
  sorting: initialSorting = [{ id: "eze_ecd_flag", desc: false }, { id: "eze_geneva_alias", desc: false }],
  setSorting: setParentSorting,
}) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  // State Management
  const [savedGlobalFilter, setSavedGlobalFilter] = useFilterState(
    "ecdScrubEzeTableGlobalFilter",
    initialGlobalFilter,
  );
  const [sorting, setSorting] = useSortingState(
    "ecdScrubEzeTableScrubSorting",
    initialSorting,
  );

  // Sync State with Parent
  useEffect(() => {
    if (setParentGlobalFilter) {
      setParentGlobalFilter(savedGlobalFilter);
    }
  }, [savedGlobalFilter, setParentGlobalFilter]);

  useEffect(() => {
    if (setParentSorting) {
      setParentSorting(sorting);
    }
  }, [sorting, setParentSorting]);

  const table = useMaterialReactTable({
    columns: getEcdEzeScrubColumns(theme, savedGlobalFilter),
    data: data || [],
    enableColumnDragging: false,
    enableSorting: true,
    enableColumnOrdering: false,
    enablePagination: false,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enableColumnResizing: false,
    enableBottomToolbar: false,
    enablePinning: true,
    enableGlobalFilter: true,
    globalFilterFn: "contains",
    columnResizeMode: "onChange",
    enableTopToolbar: false,
    enableColumnActions: false,
    enableRowVirtualization: true,
    displayColumnDefOptions: {
      "mrt-row-numbers": {
        enableColumnOrdering: false,
        enableSorting: false,
      },
    },
    sortingDisplay: "none",
    muiTableBodyRowProps: {
      sx: {
        opacity: 1,
        backgroundColor: theme.palette.background.paper,
        padding: "0px",
        height: {
          xs: "30px", // Taller rows for mobile readability
          sm: "25px",
        },
        transition: "background-color 0.2s ease",
        "&:hover": {
          backgroundColor: theme.palette.grey[100],
        },
        "&:nth-of-type(odd)": {
          backgroundColor: theme.palette.grey[100],
        },
      },
    },
    muiTableFooterRowProps: {
      sx: {
        backgroundColor: theme.palette.primary.dark,
        fontWeight: 600,
        position: "sticky",
        bottom: 0,
        zIndex: 100, // Increased zIndex to ensure visibility
        borderTop: `2px solid ${theme.palette.divider}`,
        boxSizing: "border-box",
        height: "40px", // Consistent footer height
      },
    },
    muiTableProps: {
      sx: {
        width: "100%", // Use full width instead of minWidth
        tableLayout: "fixed", // Changed from "auto" to "fixed" for better column distribution
        "& .MuiTableHead-root": {
          borderBottom: `2px solid ${theme.palette.divider}`,
        },
        "& .MuiTableHead-root .MuiTableRow-root": {
          height: "auto",
          minHeight: {
            xs: "40px", // Taller header for mobile
            sm: "35px",
          },
        },
        "& .MuiTableFooter-root": {
          borderTop: `2px solid ${theme.palette.divider}`,
          position: "sticky",
          bottom: 0,
          zIndex: 100, // Ensure footer stays above content
          backgroundColor: theme.palette.primary.dark,
        },
        "& .MuiTableFooter-root .MuiTableRow-root": {
          height: "40px",
        },
        "& .MuiTableFooter-root .MuiTableCell-root": {
          fontSize: {
            xs: "0.75rem",
            sm: "0.875rem",
          },
          color: theme.palette.primary.contrastText,
          padding: {
            xs: theme.spacing(0.5, 0.25),
            sm: theme.spacing(0.75, 0.5),
          },
          lineHeight: "1.2",
          backgroundColor: theme.palette.primary.dark,
        },
      },
    },
    initialState: {
      sorting: sorting,
      density: "compact",
      columnVisibility: {
        created_at: false,
        updated_at: false,
      },
    },
    state: {
      sorting: sorting,
      globalFilter: savedGlobalFilter,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setSavedGlobalFilter,
  });

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <div style={{ padding: "16px", borderBottom: "1px solid #ddd" }}>
        <h3 style={{ margin: 0, fontSize: "1.25rem", fontWeight: 600 }}>
          {title}
        </h3>
      </div>
      <div style={{ flex: 1, overflow: "auto" }}>
        <MaterialReactTable
          table={table}
          muiTableContainerProps={{
            sx: {
              maxHeight: "calc(100vh - 400px)",
              overflow: "auto",
            },
          }}
          muiTablePaperProps={{
            sx: {
              position: "relative",
            },
          }}
        />
      </div>
    </div>
  );
};

const ECDScrubGeneva = ({
  data,
  title = "ECD Scrub Geneva",
  globalFilter: initialGlobalFilter = "",
  setGlobalFilter: setParentGlobalFilter,
  sorting: initialSorting = [{ id: "geneva_ecd_flag", desc: false }, { id: "geneva_code", desc: false }],
  setSorting: setParentSorting,
}) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  // State Management
  const [savedGlobalFilter, setSavedGlobalFilter] = useFilterState(
    "ecdScrubGenevaTableGlobalFilter",
    initialGlobalFilter,
  );
  const [sorting, setSorting] = useSortingState(
    "ecdScrubGenevaTableScrubSorting",
    initialSorting,
  );

  // Sync State with Parent
  useEffect(() => {
    if (setParentGlobalFilter) {
      setParentGlobalFilter(savedGlobalFilter);
    }
  }, [savedGlobalFilter, setParentGlobalFilter]);

  useEffect(() => {
    if (setParentSorting) {
      setParentSorting(sorting);
    }
  }, [sorting, setParentSorting]);

  const table = useMaterialReactTable({
    columns: getEcdGenevaScrubColumns(theme, savedGlobalFilter),
    data: data || [],
    enableColumnDragging: false,
    enableSorting: true,
    enableColumnOrdering: false,
    enablePagination: false,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enableColumnResizing: false,
    enableBottomToolbar: false,
    enablePinning: true,
    enableGlobalFilter: true,
    globalFilterFn: "contains",
    columnResizeMode: "onChange",
    enableTopToolbar: false,
    enableColumnActions: false,
    enableRowVirtualization: true,
    displayColumnDefOptions: {
      "mrt-row-numbers": {
        enableColumnOrdering: false,
        enableSorting: false,
      },
    },
    sortingDisplay: "none",
    muiTableBodyRowProps: {
      sx: {
        opacity: 1,
        backgroundColor: theme.palette.background.paper,
        padding: "0px",
        height: {
          xs: "30px", // Taller rows for mobile readability
          sm: "25px",
        },
        transition: "background-color 0.2s ease",
        "&:hover": {
          backgroundColor: theme.palette.grey[100],
        },
        "&:nth-of-type(odd)": {
          backgroundColor: theme.palette.grey[100],
        },
      },
    },
    muiTableFooterRowProps: {
      sx: {
        backgroundColor: theme.palette.primary.dark,
        fontWeight: 600,
        position: "sticky",
        bottom: 0,
        zIndex: 100, // Increased zIndex to ensure visibility
        borderTop: `2px solid ${theme.palette.divider}`,
        boxSizing: "border-box",
        height: "40px", // Consistent footer height
      },
    },
    muiTableProps: {
      sx: {
        width: "100%", // Use full width instead of minWidth
        tableLayout: "fixed", // Changed from "auto" to "fixed" for better column distribution
        "& .MuiTableHead-root": {
          borderBottom: `2px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.primary.dark,
          color: theme.palette.primary.contrastText,
          fontWeight: 600,
          fontSize: {
            xs: "0.75rem",
            sm: "0.875rem",
          },
          "& .MuiTableCell-root": {
            padding: {
              xs: theme.spacing(0.5, 0.25),
              sm: theme.spacing(1, 0.5),
            },
            lineHeight: "1.2",
            borderBottom: `2px solid ${theme.palette.divider}`,
          },
        },
        "& .MuiTableBody-root .MuiTableCell-root": {
          padding: {
            xs: theme.spacing(0.5, 0.25),
            sm: theme.spacing(0.75, 0.5),
          },
          lineHeight: "1.2",
          fontSize: {
            xs: "0.75rem",
            sm: "0.875rem",
          },
          borderBottom: `1px solid ${theme.palette.divider}`,
        },
        "& .MuiTableFooter-root": {
          borderTop: `2px solid ${theme.palette.divider}`,
          position: "sticky",
          bottom: 0,
          zIndex: 100, // Ensure footer stays above content
          backgroundColor: theme.palette.primary.dark,
        },
        "& .MuiTableFooter-root .MuiTableRow-root": {
          height: "40px",
        },
        "& .MuiTableFooter-root .MuiTableCell-root": {
          fontSize: {
            xs: "0.75rem",
            sm: "0.875rem",
          },
          color: theme.palette.primary.contrastText,
          padding: {
            xs: theme.spacing(1, 0.5),
            sm: theme.spacing(1.5, 1),
          },
          lineHeight: "1.2",
          backgroundColor: theme.palette.primary.dark,
        },
      },
    },
    initialState: {
      sorting: sorting,
      density: "compact",
      columnVisibility: {
        created_at: false,
        updated_at: false,
      },
    },
    state: {
      sorting: sorting,
      globalFilter: savedGlobalFilter,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setSavedGlobalFilter,
  });

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <div style={{ padding: "16px", borderBottom: "1px solid #ddd" }}>
        <h3 style={{ margin: 0, fontSize: "1.25rem", fontWeight: 600 }}>
          {title}
        </h3>
      </div>
      <div style={{ flex: 1, overflow: "auto" }}>
        <MaterialReactTable
          table={table}
          muiTableContainerProps={{
            sx: {
              maxHeight: "calc(100vh - 400px)",
              overflow: "auto",
            },
          }}
          muiTablePaperProps={{
            sx: {
              position: "relative",
            },
          }}
        />
      </div>
    </div>
  );
};

// Main ECDScrub component that renders both tables side by side
const ECDScrub = ({
  data,
  ezeTitle = "ECD Scrub Eze",
  genevaTitle = "ECD Scrub Geneva",
  ezeGlobalFilter: initialEzeGlobalFilter = "",
  setEzeGlobalFilter: setParentEzeGlobalFilter,
  ezeSorting: initialEzeSorting = [{ id: "eze_ecd_flag", desc: false }, { id: "eze_geneva_alias", desc: false }],
  setEzeSorting: setParentEzeSorting,
  genevaGlobalFilter: initialGenevaGlobalFilter = "",
  setGenevaGlobalFilter: setParentGenevaGlobalFilter,
  genevaSorting: initialGenevaSorting = [{ id: "geneva_ecd_flag", desc: false }, { id: "geneva_code", desc: false }],
  setGenevaSorting: setParentGenevaSorting,
}) => {
  const [ecdScrubData, setEcdScrubData] = useState({ ezeData: [], genevaData: [] });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchEcdData = async () => {
      try {
        const [ezeData, genevaData] = await Promise.all([
          fetchEzeEcdScrubData(),
          fetchGenevaEcdScrubData(),
        ]);
        
        setEcdScrubData({
          ezeData: ezeData || [],
          genevaData: genevaData || []
        });
      } catch (error) {
        console.error("Failed to fetch ECD scrub data:", error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchEcdData();
  }, []);

  if (isLoading) {
    return <div>Loading ECD Scrub data...</div>;
  }

  // Extract eze and geneva data from the data prop
  const ezeData = ecdScrubData?.ezeData || [];
  const genevaData = ecdScrubData?.genevaData || [];
  
  return (
    <div style={{ display: "flex", flexDirection: "row", gap: "24px", height: "100%" }}>
      <div style={{ flex: 1 }}>
        <ECDScrubEze
          data={ezeData}
          title={ezeTitle}
          globalFilter={initialEzeGlobalFilter}
          setGlobalFilter={setParentEzeGlobalFilter}
          sorting={initialEzeSorting}
          setSorting={setParentEzeSorting}
        />
      </div>
      <div style={{ flex: 1 }}>
        <ECDScrubGeneva
          data={genevaData}
          title={genevaTitle}
          globalFilter={initialGenevaGlobalFilter}
          setGlobalFilter={setParentGenevaGlobalFilter}
          sorting={initialGenevaSorting}
          setSorting={setParentGenevaSorting}
        />
      </div>
    </div>
  );
};

// Export both individual components and the main component
export { ECDScrubEze, ECDScrubGeneva };
export default ECDScrub;